import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppColors } from "../../utility/AppColors";
import {
  FlatList,
  Image,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { useLiveStream } from "../../context/LiveStreamContext";
import { toast } from "react-toastify";
import LikeAnimation from "./LikeAnimation";
import StreamActionIcon from "../StreamActionIcons";

interface MobileStreamChatProps {
  onOpenGiftSheet: () => void;
  onOpenShareModal: () => void;
}

const MobileStreamChat: React.FC<MobileStreamChatProps> = ({
  onOpenGiftSheet,
  onOpenShareModal,
}) => {
  const flatListRef = useRef<any>(null);
  const inputRef = useRef<any>(null);
  const [likeAnimations, setLikeAnimations] = useState<number[]>([]);
  const {
    audienceData,
    sendComment,
    userData,
    currentEventId,
    totalGiftedTokens,
    viewerDetails,
    totalViewers,
    sendLike,
    setAudienceData,
    setOrganizerData,
    alreadyLiked,
  } = useLiveStream();
  const [newMessage, setNewMessage] = useState("");
  const [pendingMessages, setPendingMessages] = useState<any[]>([]);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const audienceChatData = useMemo(() => {
    const chats = audienceData?.chats || [];
    return chats
      .filter((chat: any) => chat.commentType === "Audience")
      .sort((a: any, b: any) => a.timestamp - b.timestamp);
  }, [audienceData?.chats]);

  const audienceCommentIds = useMemo(() => {
    return new Set((audienceChatData || []).map((c: any) => c.commentId));
  }, [audienceChatData]);

  // Drop pending messages once they are echoed back by server
  useEffect(() => {
    if (pendingMessages.length === 0) return;
    setPendingMessages((prev) =>
      prev.filter((m) => !audienceCommentIds.has(m.commentId))
    );
  }, [audienceCommentIds]);

  const streamChatData = useMemo(() => {
    const combined = [...audienceChatData, ...pendingMessages];
    const byId = new Map<string, any>();
    combined.forEach((m) => {
      if (!byId.has(m.commentId)) byId.set(m.commentId, m);
    });
    return Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [audienceChatData, pendingMessages]);

  const handleSendComment = useCallback(() => {
    if (newMessage.trim() === "") {
      toast.error("Please enter a message");
      return;
    }

    const userId = userData?.userID;
    const eventId = currentEventId;
    if (!userId || !eventId) {
      toast.error("Unable to send message. Try reloading the stream.");
      return;
    }

    const newComment = {
      userId,
      eventId,
      commentData: newMessage,
      commentType: "Audience",
      timestamp: Date.now(),
      profilePicture: userData?.profilePicture || "",
      commentId: `${userId}-${Date.now()}`,
    };
    setPendingMessages((prev) => [...prev, newComment]);

    sendComment(userId, eventId, newMessage, "Audience");

    setNewMessage("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [
    currentEventId,
    newMessage,
    sendComment,
    userData?.profilePicture,
    userData?.userID,
  ]);

  const handleLikeClick = useCallback(() => {
    // Add new animation
    const animationId = Date.now();
    setLikeAnimations((prev) => [...prev, animationId]);

    if (!alreadyLiked) {
      setOrganizerData((prev: any) => ({
        ...prev,
        likes: (prev?.likes || 0) + 1,
      }));

      setAudienceData((prev: any) => ({
        ...prev,
        likes: (prev?.likes || 0) + 1,
      }));
    }

    sendLike();
  }, [alreadyLiked, sendLike, setAudienceData, setOrganizerData]);

  const handleAnimationComplete = useCallback((animationId: number) => {
    setLikeAnimations((prev) => prev.filter((id) => id !== animationId));
  }, []);

  // Handle scroll position detection
  const handleScroll = useCallback((event: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const paddingToBottom = 20;
    const isBottom =
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom;

    setIsAtBottom(isBottom);
    setShowScrollToBottom(!isBottom && streamChatData.length > 0);
  }, [streamChatData.length]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (!flatListRef.current || streamChatData.length === 0) return;
    try {
      flatListRef.current.scrollToEnd({ animated: true });
      setShowScrollToBottom(false);
    } catch (e) {
      console.error("Error scrolling to bottom:", e);
    }
  }, [streamChatData.length]);

  // Auto-scroll only when user is at bottom
  const scrollTimerRef = useRef<any>(null);
  useEffect(() => {
    if (!flatListRef.current || streamChatData.length === 0 || !isAtBottom) return;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      try {
        flatListRef.current.scrollToEnd({ animated: true });
      } catch (e) {}
    }, 100);
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };
  }, [streamChatData.length, isAtBottom]);

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: "#000000",
      }}
    >
      {/* Likes & Viewers Section */}
      <View
        style={{
          display: "flex",
          height: 39,
          paddingHorizontal: 16,
          paddingVertical: 6,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          borderRadius: 6,
          borderWidth: 0.4,
          borderColor: "#535353",
          backgroundColor: "#000000",
          marginHorizontal: 12,
          marginVertical: 8,
        }}
      >
        {/* Likes */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Text style={{ color: "white", fontSize: 12, opacity: 0.7 }}>
            Likes
          </Text>
          <Text style={{ color: "white", fontSize: 16, fontWeight: "600" }}>
            {audienceData?.likes ?? 0}
          </Text>
        </View>

        {/* Divider */}
        <View
          style={{
            width: 1,
            height: 24,
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        />

        {/* Total Viewers */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Text style={{ color: "white", fontSize: 12, opacity: 0.7 }}>
            Total Viewers:
          </Text>
          <Text style={{ color: "white", fontSize: 16, fontWeight: "600" }}>
            {totalViewers ?? 0}
          </Text>
          {viewerDetails.length > 0 && (
            <View style={{ flexDirection: "row", marginLeft: 4 }}>
              {(() => {
                const unique = [
                  ...new Map(
                    viewerDetails.map((d: any) => [d.userId, d])
                  ).values(),
                ].slice(0, 7);
                return unique.map((details: any, index: number) => (
                  <Image
                    key={details.userId}
                    source={
                      details.profilePicture
                        ? { uri: details.profilePicture }
                        : AppImages.Ellipse1
                    }
                    style={{
                      height: 18,
                      width: 18,
                      borderRadius: 9,
                      marginLeft: index > 0 ? -6 : 0,
                      borderWidth: 1,
                      borderColor: "#000",
                    }}
                    resizeMode="cover"
                  />
                ));
              })()}
            </View>
          )}
        </View>

        {/* Gifts */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Image
            source={AppImages.icGift}
            style={{ height: 18, width: 18 }}
            resizeMode="contain"
          />
          <Text style={{ color: "white", fontSize: 16, fontWeight: "600" }}>
            {totalGiftedTokens ?? 0}
          </Text>
        </View>
      </View>

      {/* Chat Messages - Flex to fill remaining space */}
      <View
        style={{
          flex: 1,
          backgroundColor: "#000000",
          paddingHorizontal: 12,
        }}
      >
        <FlatList
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          ref={flatListRef}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          contentContainerStyle={{
            paddingVertical: 4,
          }}
          data={streamChatData}
          keyExtractor={(item: any) => item.commentId}
          renderItem={({ item}) => (
            <View
              style={{
                flexDirection: "row",
                marginVertical: 4,
                gap: 10,
              }}
            >
              <Image
                source={
                  item.profilePicture
                    ? { uri: item.profilePicture }
                    : AppImages.Ellipse1
                }
                style={{
                  width: 27,
                  height: 27,
                  borderRadius: 13.5,
                }}
              />
              <View style={{ flex: 1 }}>
                <Text>
                  <Text
                    style={{
                      color: "white",
                      fontSize: 16,
                      fontWeight: "600",
                    }}
                  >
                    {item.userId}
                  </Text>
                  <Text
                    style={{
                      color: "white",
                      fontSize: 12,
                      opacity: 0.7,
                    }}
                  >
                    : {item.commentData}
                  </Text>
                </Text>
              </View>
            </View>
          )}
        />

        {/* Scroll to Bottom Button */}
        {showScrollToBottom && (
          <TouchableOpacity
            onPress={scrollToBottom}
            style={{
              position: "absolute",
              bottom: 10,
              right: 10,
              backgroundColor: "rgba(220, 38, 38, 0.9)",
              borderRadius: 20,
              width: 40,
              height: 40,
              justifyContent: "center",
              alignItems: "center",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
              elevation: 5,
              zIndex: 1000,
            }}
          >
            <Text style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>
              ↓
            </Text>
          </TouchableOpacity>
        )}

        {/* Like Animations */}
        {likeAnimations.map((animationId) => (
          <LikeAnimation
            key={animationId}
            onComplete={() => handleAnimationComplete(animationId)}
          />
        ))}
      </View>

      {/* Input + Action Buttons */}
      <View
        style={{
          paddingHorizontal: 12,
          paddingTop: 8,
          paddingBottom: 8,
          backgroundColor: "#000000",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            gap: 8,
            alignItems: "center",
          }}
        >
          {/* Text Input with Send Button Inside */}
          <View
            style={{
              flex: 1,
              height: 40,
              position: "relative",
            }}
          >
            <TextInput
              ref={inputRef}
              style={{
                flex: 1,
                height: 40,
                paddingVertical: 0,
                paddingHorizontal: 16,
                borderRadius: 8,
                paddingRight: 50,
                backgroundColor: "#1F2937",
                color: "#FFFFFF",
                fontSize: 14,
                borderWidth: 0.8,
                borderColor: "#374151",
              }}
              multiline={false}
              placeholderTextColor="#999999"
              placeholder="Write Here"
              value={newMessage}
              onChangeText={setNewMessage}
              onSubmitEditing={handleSendComment}
            />

            {/* Send Button - Absolute Positioned Inside Input - Only show when typing */}
            {newMessage.trim().length > 0 && (
              <View
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  justifyContent: "center",
                }}
              >
                <StreamActionIcon type="send" onPress={handleSendComment} />
              </View>
            )}
          </View>

          {/* Gift Button */}
          <StreamActionIcon type="gift" onPress={onOpenGiftSheet} />

          {/* Like Button */}
          <StreamActionIcon type="like" onPress={handleLikeClick} />

          {/* Share Button */}
          <StreamActionIcon type="share" onPress={onOpenShareModal} />
        </View>
      </View>
    </View>
  );
};

export default MobileStreamChat;
